import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation strings
const translations = {
  en: {
    // Welcome and main interface
    welcomeMessage: "Hey! I'm Maverick AI. I'm an artificial intelligence and still learning - please verify my answers!",
    connectedTo: "Connected to Maverick AI",
    newChat: "New Chat",
    signIn: "Sign In",
    yourMessage: "Your message...",
    send: "Send",
    errorMessage: "Sorry, I encountered an error. Please try again.",
    
    // Auth modal
    createAccount: "Create Account",
    signInTitle: "Sign In",
    email: "Email",
    password: "Password",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    register: "Register",
    emailConfirmation: "Please check your email to confirm your account. After confirmation, you can sign in.",
    
    // Session expired
    sessionExpired: "Session Expired",
    sessionExpiredMessage: "Your session has expired. Please log in again to continue.",
    logIn: "Log In",
    
    // Logged-in user interface (keeping some in German as per requirements)
    conversations: "Conversations",
    currentChat: "Current Chat",
    newChatTitle: "New Chat",
    settings: "Settings",
    signOut: "Sign Out",
    status: "Status",
    administrator: "Administrator",
    user: "User",
    
    // Chat features (for authenticated users)
    sources: "Sources",
    reasoning: "Reasoning",
    
    // Feedback
    feedbackTitle: "Give Feedback",
    feedbackType: "Type of Feedback",
    pleaseSelect: "Please select",
    incorrectAnswer: "Answer is incorrect",
    unclearAnswer: "Answer is unclear", 
    incompleteAnswer: "Answer is incomplete",
    irrelevantAnswer: "Answer is irrelevant",
    other: "Other",
    additionalComments: "Additional Comments",
    describeFeedback: "Describe your feedback in more detail...",
    cancel: "Cancel",
    sendFeedback: "Send Feedback",
    sending: "Sending..."
  },
  de: {
    // Welcome and main interface
    welcomeMessage: "Hey! Ich bin Maverick AI. Ich bin eine Künstliche Intelligenz und lerne noch - bitte überprüfen Sie meine Antworten!",
    connectedTo: "Verbunden mit Maverick AI",
    newChat: "Neuer Chat",
    signIn: "Anmelden",
    yourMessage: "Ihre Nachricht...",
    send: "Senden",
    errorMessage: "Entschuldigung, ich habe einen Fehler festgestellt. Bitte versuchen Sie es erneut.",
    
    // Auth modal
    createAccount: "Konto erstellen",
    signInTitle: "Anmelden",
    email: "E-Mail",
    password: "Passwort",
    alreadyHaveAccount: "Haben Sie bereits ein Konto?",
    dontHaveAccount: "Noch kein Konto?",
    register: "Registrieren",
    emailConfirmation: "Bitte überprüfen Sie Ihre E-Mail, um Ihr Konto zu bestätigen. Nach der Bestätigung können Sie sich anmelden.",
    
    // Session expired
    sessionExpired: "Sitzung abgelaufen",
    sessionExpiredMessage: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.",
    logIn: "Anmelden",
    
    // Logged-in user interface
    conversations: "Gespräche",
    currentChat: "Aktueller Chat",
    newChatTitle: "Neuer Chat",
    settings: "Einstellungen",
    signOut: "Abmelden",
    status: "Status",
    administrator: "Administrator",
    user: "Benutzer",
    
    // Chat features (for authenticated users)
    sources: "Quellen",
    reasoning: "Gedankengang",
    
    // Feedback
    feedbackTitle: "Feedback geben",
    feedbackType: "Art des Feedbacks",
    pleaseSelect: "Bitte wählen",
    incorrectAnswer: "Antwort ist falsch",
    unclearAnswer: "Antwort ist unklar",
    incompleteAnswer: "Antwort ist unvollständig",
    irrelevantAnswer: "Antwort ist irrelevant",
    other: "Sonstiges",
    additionalComments: "Zusätzliche Anmerkungen",
    describeFeedback: "Beschreiben Sie Ihr Feedback genauer...",
    cancel: "Abbrechen",
    sendFeedback: "Feedback senden",
    sending: "Wird gesendet..."
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to English
    return localStorage.getItem('ui-language') || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('ui-language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const switchLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}; 